import {
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { memo, useEffect, useState } from 'react'
import { Add as AddIcon } from '@material-ui/icons'
import { useHistory, useParams } from 'react-router'
import { RoutePaths } from 'containers/Core/Routes'
import useEligibleInsurances, { Insurance } from 'hooks/useEligibleInsurance'
import EligibleInsuranceTableRow from './EligibleInsuranceTableRow'
import { useAuthContext } from 'context/AuthContext/AuthProvider'
import { Permissions } from 'containers/Core/utils'
import NewEligibleInsuranceDialog from './NewEligibleInsuranceDialog'
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk'

interface EligibleInsuranceParams {
  provider?: string
}

const EligibleInsuranceTable: React.FC = () => {
  const history = useHistory()
  const { permissions } = useAuthContext()
  const { insurances } = useEligibleInsurances()
  const { provider } = useParams<EligibleInsuranceParams>()
  const [editInsurance, setEditInsurance] = useState<Insurance | undefined>(
    undefined
  )

  const openAddNewDialog = () =>
    history.push(`${RoutePaths.DASHBOARD_ELIGIBLE_INSURANCES}/new`)

  const closeDialog = () => {
    history.push(RoutePaths.DASHBOARD_ELIGIBLE_INSURANCES)
    setEditInsurance(undefined)
  }

  useEffect(() => {
    if (Object.keys(insurances).length && provider && provider !== 'new') {
      const insurance = insurances[provider]

      if (insurance) {
        setEditInsurance(insurance)
      }
    }
  }, [insurances, provider])

  return (
    <div>
      <TableContainer className="table-container">
        <div className="d-flex justify-content-end">
          {permissions.includes(Permissions.ADD_ELIGIBLE_INSURANCES) && (
            <Button
              variant="contained"
              className="add-new-button"
              startIcon={<AddIcon />}
              onClick={openAddNewDialog}
            >
              Add New
            </Button>
          )}
        </div>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>States</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.values(insurances).map((insurance) => (
              <EligibleInsuranceTableRow
                key={insurance.provider}
                insurance={insurance}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <NewEligibleInsuranceDialog
        open={provider !== undefined}
        handleClose={closeDialog}
        insurance={editInsurance}
      />
    </div>
  )
}

const EligibleInsuranceList: React.FC = () => {
  const ldClient = useLDClient()
  const [ldReady, setLdReady] = useState(false)
  const { eligibleInsurancesFromCareops } = useFlags()
  const sourceBaseUrl = process.env.REACT_APP_SOURCE_BASE_URL

  useEffect(() => {
    if (!ldClient) {
      setLdReady(true) // fail open — no client means we can't gate on the flag
      return
    }

    ldClient
      .waitForInitialization()
      .then(() => setLdReady(true))
      .catch(() => setLdReady(true)) // fail open on LD error
  }, [ldClient])

  if (!ldReady) {
    return null
  }

  if (eligibleInsurancesFromCareops) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          severity="info"
          action={
            sourceBaseUrl ? (
              <a
                href={`${sourceBaseUrl}/settings/payers`}
                target="_blank"
                rel="noreferrer"
                style={{ whiteSpace: 'nowrap', fontWeight: 600 }}
              >
                Manage in CareOps →
              </a>
            ) : undefined
          }
        >
          Eligibility by state/region is now determined via SourceHealth/CareOps
          payers. This page is no longer the source of truth.
        </Alert>
      </div>
    )
  }

  return <EligibleInsuranceTable />
}

export default memo(EligibleInsuranceList)
